import React, { Component } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'

import Panel from '../../../UniformComponents/Panel'
import Button from '../../../UniformComponents/Button'
import BubbleLoader from '../../../UniformComponents/BubbleLoader'
import Switch from '../../../UniformComponents/Switch'
import Select from '../../form/Select'
import Input from '../../form/Input'
import DataTable from '../../DataTable'

import Store, {
    ENTITY_TYPES, STATUS_FILTER_ALL, STATUS_FILTER_ENABLED, STATUS_FILTER_DISABLED,
} from './store'

import './style.scss'

@observer
class PresentationAccessAdmin extends Component {
    constructor(props) {
        super(props)
        this.store = new Store(props.store)
    }

    componentDidMount() {
        const { store: appStore } = this.props

        appStore.breadcrumps = [
            {
                name: 'intranet',
                url: '/intranet',
                title: i18n.t('intranet.presentationAccessAdmin.breadcrumb.intranet'),
            },
            {
                name: 'presentationAccessAdmin',
                url: '/intranet/presentationAccessAdmin',
                title: i18n.t('intranet.presentationAccessAdmin.breadcrumb.page'),
            },
        ]

        this.store.init()
    }

    getClientLevelOptions = () => ([
        { value: 0, label: i18n.t('intranet.presentationAccessAdmin.clientLevel.options.none') },
        { value: 1, label: i18n.t('intranet.presentationAccessAdmin.clientLevel.options.limited') },
        { value: 2, label: i18n.t('intranet.presentationAccessAdmin.clientLevel.options.full') },
    ])

    getStatusFilterOptions = () => ([
        { value: STATUS_FILTER_ALL, label: i18n.t('intranet.presentationAccessAdmin.filters.statusAll') },
        { value: STATUS_FILTER_ENABLED, label: i18n.t('intranet.presentationAccessAdmin.filters.statusEnabled') },
        { value: STATUS_FILTER_DISABLED, label: i18n.t('intranet.presentationAccessAdmin.filters.statusDisabled') },
    ])

    getColumns = (entityType) => ([
        {
            name: i18n.t('intranet.presentationAccessAdmin.columns.id'),
            key: 'id',
        },
        {
            name: i18n.t('intranet.presentationAccessAdmin.columns.name'),
            key: 'name',
        },
        {
            name: i18n.t('intranet.presentationAccessAdmin.columns.access'),
            key: 'effectiveEnabled',
            dataTransform: (_value, row) => (
                <div className="access-cell">
                    <Switch
                        state={row.effectiveEnabled}
                        disabled={!row.editable}
                        optimisticUpdate={false}
                        onToggle={(next) => this.store.toggleEntity(entityType, row.id, next)}
                    />
                    {!row.editable && row.reason && (
                        <p className="access-reason">{row.reason.message}</p>
                    )}
                </div>
            ),
        },
    ])

    renderTabButtons = () => {
        const { store } = this

        return (
            <ul className="tabs">
                {ENTITY_TYPES.map((entityType) => (
                    <li key={entityType}>
                        <Button
                            type="button"
                            cssClass={store.activeTab === entityType ? 'active' : ''}
                            onClick={() => store.activateTab(entityType)}
                            label={i18n.t(`intranet.presentationAccessAdmin.tabs.${entityType}`)}
                        />
                    </li>
                ))}
            </ul>
        )
    }

    renderFilters = (entityType) => {
        const { store } = this
        const filters = store.filters[entityType]

        return (
            <div className="presentation-access-filters">
                <Input
                    name={`${entityType}-id-filter`}
                    label={i18n.t('intranet.presentationAccessAdmin.filters.idLabel')}
                    placeholder={i18n.t('intranet.presentationAccessAdmin.filters.idPlaceholder')}
                    value={filters.id}
                    onChange={({ value }) => store.changeFilter(entityType, 'id', value)}
                />
                <Input
                    name={`${entityType}-name-filter`}
                    label={i18n.t('intranet.presentationAccessAdmin.filters.nameLabel')}
                    placeholder={i18n.t('intranet.presentationAccessAdmin.filters.namePlaceholder')}
                    value={filters.name}
                    onChange={({ value }) => store.changeFilter(entityType, 'name', value)}
                />
                <Select
                    name={`${entityType}-status-filter`}
                    label={i18n.t('intranet.presentationAccessAdmin.filters.statusLabel')}
                    value={filters.status}
                    options={this.getStatusFilterOptions()}
                    onBlur={({ value }) => store.changeFilter(entityType, 'status', value)}
                    isClearable={false}
                />
            </div>
        )
    }

    renderNotConfigurable = () => {
        const { store } = this
        const key = store.clientLevel === 2
            ? 'intranet.presentationAccessAdmin.notConfigurable.full'
            : 'intranet.presentationAccessAdmin.notConfigurable.none'

        return (
            <p className="not-configurable">{i18n.t(key)}</p>
        )
    }

    renderTabContent = () => {
        const { store } = this
        const { activeTab } = store
        const tab = store[activeTab]

        if (tab.loading) {
            return <BubbleLoader cssClass="loader" />
        }

        if (tab.error) {
            return <p className="tab-error">{tab.error}</p>
        }

        if (!tab.configurable) {
            return this.renderNotConfigurable()
        }

        return (
            <>
                {this.renderFilters(activeTab)}
                <DataTable
                    inMemory
                    dataSet={store.getFilteredItems(activeTab)}
                    columns={this.getColumns(activeTab)}
                    rowId="id"
                />
            </>
        )
    }

    render() {
        const { store } = this

        return (
            <Panel cssClass="presentationAccessAdmin">
                <h1>{i18n.t('intranet.presentationAccessAdmin.title')}</h1>

                {store.clientLevelLoading ? (
                    <BubbleLoader cssClass="loader" />
                ) : (
                    <>
                        {store.clientLevelError && (
                            <p className="client-level-error">{store.clientLevelError}</p>
                        )}
                        <Select
                            name="clientLevel"
                            label={i18n.t('intranet.presentationAccessAdmin.clientLevel.label')}
                            value={store.clientLevel}
                            options={this.getClientLevelOptions()}
                            onBlur={store.changeClientLevel}
                            showDisabled={store.clientLevelSaving}
                            isClearable={false}
                        />
                    </>
                )}

                {this.renderTabButtons()}
                {this.renderTabContent()}
            </Panel>
        )
    }
}

export default PresentationAccessAdmin
